import React from 'react';
import { Box, Typography } from '@mui/material';

type FilterPillSliderProps<T extends string> = {
  title: string;
  levels: readonly T[];
  labels: Record<T, string>;
  value: T | null;
  onChange: (value: T | null) => void;
  disabled?: boolean;
};

function FilterPillSlider<T extends string>({
  title,
  levels,
  labels,
  value,
  onChange,
  disabled = false,
}: FilterPillSliderProps<T>) {
  const isActive = value !== null;
  const selectedLevel = value ?? levels[0];
  const selectedIndex = Math.max(0, levels.indexOf(selectedLevel));
  const toggle = () => {
    if (disabled) return;
    onChange(isActive ? null : levels[0]);
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    toggle();
  };

  return (
    <Box sx={{ minWidth: 180, width: '100%', maxWidth: 220 }}>
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-pressed={isActive}
        onClick={toggle}
        onKeyDown={handleKeyDown}
        sx={{
          position: 'relative',
          width: '100%',
          py: 1,
          px: 1.25,
          borderRadius: 6,
          border: '0px solid',
          borderColor: isActive ? 'primary.main' : 'rgba(148,163,184,0.45)',
          bgcolor: isActive ? 'background.paper' : 'grey.100',
          color: isActive ? 'text.primary' : 'text.secondary',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.55 : 1,
          transition: 'background-color 150ms ease, border-color 150ms ease',
          '&:hover': disabled
            ? undefined
            : {
                borderColor: isActive ? 'primary.main' : 'text.secondary',
                bgcolor: isActive ? 'background.paper' : 'grey.100',
              },
        }}
      >
        <Typography
          variant="subtitle2"
          align="center"
          sx={{
            mb: 0.75,
            color: isActive ? 'text.primary' : 'text.secondary',
            fontWeight: 600,
          }}
        >
          {title}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${levels.length}, 1fr)`,
            mb: 0.75,
          }}
        >
          {levels.map((level) => (
            <Typography
              key={level}
              variant="caption"
              align="center"
              sx={{
                color: isActive ? 'text.primary' : 'text.secondary',
                fontWeight: isActive && selectedLevel === level ? 700 : 600,
              }}
            >
              {labels[level]}
            </Typography>
          ))}
        </Box>

        <Box
          sx={{
            position: 'relative',
            height: 36,
            borderRadius: 2,
            bgcolor: isActive ? '#dbe2f2' : 'grey.200',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 4,
              left: 4,
              width: `calc((100% - 8px) / ${levels.length})`,
              height: 28,
              borderRadius: 1.5,
              bgcolor: isActive ? '#ffffff' : 'grey.50',
              boxShadow: isActive ? '0 1px 4px rgba(15,23,42,0.18)' : 'none',
              transform: `translateX(${selectedIndex * 100}%)`,
              transition: 'transform 180ms ease',
            }}
          />

          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              gridTemplateColumns: `repeat(${levels.length}, 1fr)`,
            }}
          >
            {levels.map((level) => (
              <Box
                key={level}
                component="button"
                type="button"
                disabled={disabled}
                aria-label={`Set ${title.toLowerCase()} to ${labels[level]}`}
                aria-pressed={isActive && selectedLevel === level}
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                  event.stopPropagation();
                  onChange(level);
                }}
                sx={{
                  border: 0,
                  bgcolor: 'transparent',
                  cursor: disabled ? 'default' : 'pointer',
                  p: 0,
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default FilterPillSlider;
